import ShopifyClient from './shopify';
import { TokenCart } from './TokenCart';
import {
  CartLineItem,
  EligibleReward,
  ShopifyCartAddToCartResponse,
  ShopifyCartState,
  TokenCartItem
} from './types';

export class ShopifyCart {
  shopifyClient = new ShopifyClient();

  constructor(readonly _shopifyCartState: ShopifyCartState) {}

  get state() {
    return this._shopifyCartState;
  }

  findItem(tokenCartItem: TokenCartItem) {
    return this._shopifyCartState.items.find((shopifyCartItem) =>
      this.isItemEqual(shopifyCartItem, tokenCartItem)
    );
  }

  isTokenUsed(shopifyCartItem: CartLineItem, tokenCartItem: TokenCartItem) {
    return Boolean(
      shopifyCartItem.properties?._campaignId === tokenCartItem.campaignId &&
        shopifyCartItem.properties._network === tokenCartItem.token?.network &&
        shopifyCartItem.properties._contractAddress === tokenCartItem.token?.contractAddress &&
        Boolean(shopifyCartItem.properties) &&
        shopifyCartItem.properties._tokenId === tokenCartItem.token?.tokenId &&
        shopifyCartItem.properties._isTokenGated
    );
  }

  isItemEqual(shopifyCartItem: CartLineItem, tokenCartItem: TokenCartItem) {
    return Boolean(
      shopifyCartItem.variant_id === tokenCartItem.variant_id &&
        Boolean(shopifyCartItem.properties) &&
        shopifyCartItem.properties._network === tokenCartItem.token?.network &&
        shopifyCartItem.properties._contractAddress === tokenCartItem.token?.contractAddress &&
        Boolean(shopifyCartItem.properties) &&
        shopifyCartItem.properties._tokenId === tokenCartItem.token?.tokenId &&
        shopifyCartItem.properties._isTokenGated
    );
  }

  /**
   * Syncs server stored token items with client shopify cart
   *
   * @param tokenCart         - Server token cart
   * @param cartIconSection   - Cart icon section id
   * @returns                 - undefined if no changes, ShopifyCartAddToCartResponse if there was a change
   */
  async initialSyncWithTokenCart(
    tokenCart: TokenCart | undefined,
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse | undefined> {
    let shopifyAddToCartResponse;
    // Remove all shopifyCart token gated items
    const shopifyCartTokenItems = this._shopifyCartState.items.filter(
      (shopifyCartItem) => shopifyCartItem.properties && shopifyCartItem.properties._isTokenGated
    );

    let match = false;
    if (tokenCart && shopifyCartTokenItems.length === tokenCart.items.length) {
      match = true;
      // Check if the provided tokenCart simply matches what we expect.
      // If so, nothing to remove
      shopifyCartTokenItems.sort((a, b) =>
        `${a.properties._campaignId}:${a.variant_id}:${a.properties._network}:${a.properties._contractAddress}:${a.properties._tokenId}` >
        `${b.properties._campaignId}:${b.variant_id}:${b.properties._network}:${b.properties._contractAddress}:${b.properties._tokenId}`
          ? 1
          : -1
      );
      tokenCart!.items.sort((a, b) =>
        `${a.campaignId}:${a.variant_id}:${a.token.network}:${a.token.contractAddress}:${a.token.tokenId}` >
        `${b.campaignId}:${b.variant_id}:${a.token.network}:${b.token.contractAddress}:${b.token.tokenId}`
          ? 1
          : -1
      );
      for (let i = 0; i < shopifyCartTokenItems.length; i++) {
        const shopifyCartTokenItem = shopifyCartTokenItems[i];
        const tokenCartItem = tokenCart.items[i];
        if (
          shopifyCartTokenItem.properties._campaignId !== tokenCartItem.campaignId ||
          shopifyCartTokenItem.variant_id !== tokenCartItem.variant_id ||
          shopifyCartTokenItem.properties._network !== tokenCartItem.token.network ||
          shopifyCartTokenItem.properties._contractAddress !==
            tokenCartItem.token.contractAddress ||
          shopifyCartTokenItem.properties._tokenId !== tokenCartItem.token.tokenId ||
          shopifyCartTokenItem.quantity !== tokenCartItem.quantity ||
          shopifyCartTokenItem.properties._eip5131Address !== tokenCartItem.token.eip5131Address ||
          shopifyCartTokenItem.properties._eip5639Address !== tokenCartItem.token.eip5639Address
        ) {
          match = false;
          break;
        }
      }
    }

    // Token cart items match, nothing to do
    if (match) return undefined;

    if (shopifyCartTokenItems.length > 0) {
      const cartUpdates: any = {};
      for (const removalShopifyCartItem of shopifyCartTokenItems) {
        cartUpdates[removalShopifyCartItem.key] = 0;
      }
      shopifyAddToCartResponse = await this.shopifyClient.updateBrowserCart(
        cartUpdates,
        undefined,
        cartIconSection
      );
    }
    const shopifyCartItems: any = [];
    if (tokenCart && tokenCart.items.length > 0) {
      for (const tokenCartItem of tokenCart.items) {
        const properties: CartLineItem['properties'] = {
          ...tokenCartItem.customProperties,
          _isTokenGated: true,
          _tokenId: tokenCartItem.token.tokenId,
          _network: tokenCartItem.token.network,
          _contractAddress: tokenCartItem.token.contractAddress,
          _campaignId: tokenCartItem.campaignId,
          _image: tokenCartItem.reward?.token.image,
          _maxRedemptions: tokenCartItem.reward?.limit,
          _discountPercent: tokenCartItem.reward?.reward.discountPercent,
          _discountAmount: tokenCartItem.reward?.reward.discountAmount,
          _eip5131Address: tokenCartItem.token.eip5131Address,
          _eip5639Address: tokenCartItem.token.eip5639Address,
          'NFT Used': tokenCartItem.reward?.token?.name,
          'Discount (Applied at Checkout)': this.getDiscountMessageFromReward(tokenCartItem.reward)
        };
        shopifyCartItems.push({
          id: tokenCartItem.variant_id,
          quantity: tokenCartItem.quantity,
          properties: properties
        });
      }
      shopifyAddToCartResponse = await this.shopifyClient.addToBrowserCartWithProperties(
        shopifyCartItems,
        cartIconSection
      );
    }
    return shopifyAddToCartResponse;
  }

  async removeTokenCartItems(
    tokenCartItems: TokenCartItem[],
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse | undefined> {
    if (tokenCartItems.length > 0) {
      const cartItems = this._shopifyCartState.items.filter((x) => {
        for (const tokenCartItem of tokenCartItems) {
          if (
            x.variant_id === tokenCartItem.variant_id &&
            x.properties._campaignId === tokenCartItem.campaignId &&
            x.properties._network === tokenCartItem.token.network &&
            x.properties._contractAddress === tokenCartItem.token.contractAddress &&
            x.properties._tokenId === tokenCartItem.token.tokenId &&
            // Add checks for delegate flags
            x.properties._eip5131Address === tokenCartItem.token.eip5131Address &&
            x.properties._eip5639Address === tokenCartItem.token.eip5639Address
          ) {
            return true;
          }
        }
        return false;
      });
      const cartUpdates: any = {};
      for (const cartItem of cartItems) {
        cartUpdates[cartItem.key] = 0;
      }
      return await this.shopifyClient.updateBrowserCart(cartUpdates, undefined, cartIconSection);
    }
  }

  async syncTokenCartItem(
    tokenCartItem: TokenCartItem,
    cartIconSection: string | undefined
  ): Promise<ShopifyCartAddToCartResponse> {
    let resp;
    const shopifyCartItem = this.findItem(tokenCartItem);

    // These properties are hashed to define the unique ID for a shopify cart item.
    // The keys have to start with an underscore or they will actually show up in the UI.
    //
    // Be careful adding / removing values here.
    const properties: CartLineItem['properties'] = {
      ...tokenCartItem.customProperties,
      _isTokenGated: true,
      _tokenId: tokenCartItem.token.tokenId,
      _network: tokenCartItem.token.network,
      _contractAddress: tokenCartItem.token.contractAddress,
      _campaignId: tokenCartItem.campaignId,
      _image: tokenCartItem.reward?.token.image,
      _maxRedemptions: tokenCartItem.reward?.limit,
      _discountPercent: tokenCartItem.reward?.reward.discountPercent,
      _discountAmount: tokenCartItem.reward?.reward.discountAmount,
      _eip5131Address: tokenCartItem.token.eip5131Address,
      _eip5639Address: tokenCartItem.token.eip5639Address,
      'NFT Used': tokenCartItem.reward?.token?.name,
      'Discount (Applied at Checkout)': this.getDiscountMessageFromReward(tokenCartItem.reward)
    };

    if (shopifyCartItem) {
      resp = await this.shopifyClient.changeBrowserCart(
        shopifyCartItem.key,
        properties,
        tokenCartItem.quantity,
        cartIconSection
      );
    } else {
      resp = await this.shopifyClient.addToBrowserCartWithProperties(
        [
          {
            id: tokenCartItem.variant_id,
            quantity: tokenCartItem.quantity,
            properties: properties
          }
        ],
        cartIconSection
      );
    }
    return resp;
  }

  getDiscountMessageFromReward(reward?: EligibleReward) {
    const isPercent = Boolean(reward?.reward.discountPercent);
    const isAmount = Boolean(reward?.reward.discountAmount);

    if (isPercent) {
      return `${reward?.reward.discountPercent}% off`;
    } else if (isAmount) {
      return `$${reward?.reward.discountAmount} USD off`;
    }

    return '';
  }
}
